/**
 * Project-wide constants. These are NOT business config (which lives in the DB).
 * Only put things here that are truly invariant: enum values, API paths,
 * regex patterns, etc.
 */

export const API_VERSION = 'v1' as const;
export const API_PREFIX = `/api/${API_VERSION}` as const;

export const APP_CONFIG_ENDPOINT = `${API_PREFIX}/config` as const;
export const HEALTH_ENDPOINT = `${API_PREFIX}/health` as const;

export const USER_ROLES = [
  'ADMIN',
  'SALES_LEAD',
  'SALES_AGENT',
  'OPS',
  'FINANCE',
  'HR',
  'READ_ONLY',
  // Phase 5 website CMS roles
  'CONTENT_EDITOR',
  'MARKETING_LEAD',
  // Full website-module manager (packages, pages, blogs, nav, themes,
  // category/destination pages, media).
  'WEBSITE_MANAGER',
  // SEO: same website permissions as WEBSITE_MANAGER; its package / category /
  // destination changes will route to WEBSITE_MANAGER for approval (future).
  'WEBSITE_SEO',
  // 2026-07-09. Org-chart splits + functional back-office desks
  // (ADR-0063). HR gets a lead/executive split mirroring sales; ops
  // gets its own lead/executive; VISA + TICKETING are back-office
  // roles that own their inventory master + read-only ops context.
  'HR_MANAGER',
  'HR_EXECUTIVE',
  'OPERATIONS_LEAD',
  'OPERATIONS_EXECUTIVE',
  'VISA',
  'TICKETING',
] as const;

export type UserRoleConst = (typeof USER_ROLES)[number];

/**
 * Org-chart parent role map (ADR-0039 / users hierarchy 2026-06-10).
 *
 * For each role, lists the roles whose users are valid "reporting
 * managers" when creating or updating a user of that role. Mirrors the
 * `parent` column of the seeded `roles` table - kept here so both API
 * and admin can validate / drive UI without a roundtrip.
 *
 *   ADMIN          -> []                      (root: no manager)
 *   SALES_LEAD     -> [ADMIN]
 *   SALES_AGENT    -> [SALES_LEAD]
 *   OPS            -> [ADMIN]
 *   FINANCE        -> [ADMIN]
 *   HR             -> [ADMIN]
 *   READ_ONLY      -> [ADMIN]
 *   MARKETING_LEAD -> [ADMIN]
 *   CONTENT_EDITOR -> [MARKETING_LEAD]
 */
export const ROLE_HIERARCHY: Readonly<Record<UserRoleConst, ReadonlyArray<UserRoleConst>>> = {
  ADMIN: [],
  SALES_LEAD: ['ADMIN'],
  SALES_AGENT: ['SALES_LEAD'],
  OPS: ['ADMIN'],
  FINANCE: ['ADMIN'],
  HR: ['ADMIN'],
  READ_ONLY: ['ADMIN'],
  MARKETING_LEAD: ['ADMIN'],
  CONTENT_EDITOR: ['MARKETING_LEAD'],
  WEBSITE_MANAGER: ['ADMIN'],
  WEBSITE_SEO: ['WEBSITE_MANAGER'],
  // HR desk: manager reports to ADMIN, executive to manager.
  HR_MANAGER: ['ADMIN'],
  HR_EXECUTIVE: ['HR_MANAGER'],
  // Operations desk: lead reports to ADMIN, executive to lead - or straight
  // to an ADMIN (2026-10-31: desks without an operations lead yet).
  OPERATIONS_LEAD: ['ADMIN'],
  OPERATIONS_EXECUTIVE: ['OPERATIONS_LEAD', 'ADMIN'],
  // Back-office functional desks report to OPERATIONS_LEAD (they
  // execute against ops queues).
  VISA: ['OPERATIONS_LEAD'],
  TICKETING: ['OPERATIONS_LEAD'],
};

/// Returns the roles eligible to manage the given role. Empty for ADMIN.
export const getParentRolesFor = (role: UserRoleConst): ReadonlyArray<UserRoleConst> =>
  ROLE_HIERARCHY[role] ?? [];

export const SUPPORTED_CURRENCIES_FALLBACK = ['INR', 'USD', 'EUR', 'GBP', 'AED'] as const;

export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_PAGE_SIZE: 25,
  MAX_PAGE_SIZE: 200,
} as const;

/** Ordered destination coverage on a user (ADR-0057). */
export const USER_DESTINATIONS = {
  MAX_COUNT: 50,
  MIN_PRIORITY: 1,
  /** Upper bound on a destination's leads-per-day cap. */
  MAX_DAILY_LEAD_CAPACITY: 999,
} as const;

export {
  OPERATIONS_DEPARTMENT_CODES,
  OPERATIONS_ROLES,
  SALES_DEPARTMENT_CODES,
  SALES_ROLES,
  SALES_COVERAGE_REQUIRED_MESSAGE,
  USER_COVERAGE_DEPARTMENT_CODES,
  isCoverageRole,
  isOperationsDepartmentCode,
  isOperationsRole,
  isSalesDepartmentCode,
  isSalesRole,
  isUserCoverageDepartmentCode,
  missingSalesCoverage,
  salesHaulRequired,
  requiresUserSalesCoverage,
  type OperationsDepartmentCode,
  type OperationsRole,
  type SalesCoverageCounts,
  type SalesCoverageField,
  type SalesDepartmentCode,
  type SalesRole,
  type UserCoverageDepartmentCode,
} from './sales-departments.js';
